import { defineAsyncComponent, shallowRef } from 'vue';
import { storeToRefs } from 'pinia';
import moment from '@/common/modules/moment-fallback';
import { applyArrangementFormConfig, applyFormFieldOverrides } from '@/widgets/Wizard/modules/tommy3/forms';
import { PersonalDetailsStep, PinPaymentStep, ReceiptConfirmStep } from '@/widgets/Wizard/modules/steps';
import { useMetadataStore } from '@/widgets/Wizard/stores/metadata';
import { useReservationStore } from '@/widgets/Wizard/stores/reservation';
import { useStepsStore } from '@/widgets/Wizard/stores/steps';
import type { Accommodation } from '@/widgets/Wizard/models/wizard';

export class CampingLaPinedePinPaymentStepOverride extends PinPaymentStep {
    public component = shallowRef(
        defineAsyncComponent(
            () => import('@/widgets/Wizard/components-override/camping-la-pinede/steps/book/pin-payment.vue'),
        ),
    );
}

export class UiterwaardePersonalDetailsStep extends PersonalDetailsStep {
    private metaDataStore: ReturnType<typeof useMetadataStore> | null = null;
    private reservationStore: ReturnType<typeof useReservationStore> | null = null;
    private stepsStore: ReturnType<typeof useStepsStore> | null = null;

    public setup() {
        this.metaDataStore = useMetadataStore();
        this.reservationStore = useReservationStore();
        this.stepsStore = useStepsStore();
    }

    public activate() {
        void this.loadArrangement();
    }

    private async loadArrangement() {
        if (!this.metaDataStore || !this.reservationStore || !this.stepsStore) {
            return;
        }

        const { activeAccount, accommodations, ageCategories, formConfig } = storeToRefs(this.metaDataStore);
        const {
            arrangementId,
            arrangementLoadFailed,
            chosenAccommodation,
            arrivalDate,
            totalNightsStaying,
            reservedAgeGroups,
            priceCalculation,
            departureDate,
        } = storeToRefs(this.reservationStore);
        const { showLoader } = storeToRefs(this.stepsStore);

        arrangementLoadFailed.value = false;

        const param = new URLSearchParams(window.location.search).get('arrangement');
        if (!param) {
            arrangementLoadFailed.value = true;

            return;
        }

        arrangementId.value = param;
        showLoader.value = true;

        try {
            const tommy = this.metaDataStore.getTommyGateway();
            const salesChannel = activeAccount.value ? activeAccount.value.options.salesChannel : undefined;

            const calendarInfo = await tommy.getArrangementCalendarInfo([param]);
            const arrangement = calendarInfo && calendarInfo.length > 0 ? calendarInfo[0] : null;

            if (!arrangement) {
                arrangementLoadFailed.value = true;

                return;
            }

            const accommodation = this.resolveAccommodation(accommodations.value, arrangement.accommodationId);

            if (!accommodation) {
                arrangementLoadFailed.value = true;

                return;
            }

            chosenAccommodation.value = accommodation;

            const arrival = moment(arrangement.dateFrom);
            const departure = moment(arrangement.dateTill);

            arrivalDate.value = arrival.getDateObject();
            totalNightsStaying.value = departure.diff(arrival, 'days');

            reservedAgeGroups.value = {};

            for (const ageCategory of ageCategories.value) {
                reservedAgeGroups.value[ageCategory.id.toString()] = ageCategory.defaultAmount;
            }

            const totalPax = Object.values(reservedAgeGroups.value).reduce((sum, amount) => sum + amount, 0);

            if (totalPax === 0 && ageCategories.value.length > 0) {
                const firstAgeCategory = ageCategories.value[0];

                if (firstAgeCategory) {
                    reservedAgeGroups.value[firstAgeCategory.id.toString()] = 1;
                }
            }

            if (!arrivalDate.value || !departureDate.value || totalNightsStaying.value <= 0) {
                arrangementLoadFailed.value = true;

                return;
            }

            const { i18n } = await import('@/widgets/Wizard/i18n');
            const arrangementApiId = parseInt(param, 10);
            const arrangements = Number.isNaN(arrangementApiId) ? [{ id: param }] : [{ id: arrangementApiId }];

            let price = await tommy.getPriceCalculation(
                i18n.global.locale.value.toString(),
                arrivalDate.value,
                departureDate.value,
                accommodation.id,
                [],
                reservedAgeGroups.value,
                undefined,
                undefined,
                salesChannel,
                arrangements,
            );

            if (!price) {
                price = await tommy.getPriceCalculation(
                    i18n.global.locale.value.toString(),
                    arrivalDate.value,
                    departureDate.value,
                    accommodation.id,
                    [],
                    reservedAgeGroups.value,
                    undefined,
                    undefined,
                    salesChannel,
                );
            }

            if (!price) {
                arrangementLoadFailed.value = true;

                return;
            }

            priceCalculation.value = price;

            if (!formConfig.value) {
                await this.metaDataStore.updateFormConfig(i18n.global.locale.value.toString());
            }

            if (formConfig.value) {
                if (activeAccount.value?.options.arrangementFormDefaults) {
                    formConfig.value = applyFormFieldOverrides(
                        formConfig.value,
                        activeAccount.value.options.arrangementFormDefaults,
                    );
                }

                if (activeAccount.value?.options.arrangementFormConfig) {
                    formConfig.value = applyArrangementFormConfig(
                        formConfig.value,
                        param,
                        activeAccount.value.options.arrangementFormConfig,
                    );
                }
            }
        } catch (err) {
            console.warn('Failed to load arrangement', err);
            arrangementLoadFailed.value = true;
        } finally {
            showLoader.value = false;
        }
    }

    private resolveAccommodation(
        loadedAccommodations: Accommodation[],
        accommodationId: number,
    ): Accommodation | null {
        const accommodation = loadedAccommodations.find((item) => item.id === accommodationId);

        if (accommodation) {
            return accommodation;
        }

        // Arrangement calendar-info may reference a different accommodation id than widget metadata.
        if (loadedAccommodations.length === 1) {
            return loadedAccommodations[0] ?? null;
        }

        return null;
    }
}

export class UiterwaardeReceiptConfirmStepOverride extends ReceiptConfirmStep {
    public component = shallowRef(
        defineAsyncComponent(
            () => import('@/widgets/Wizard/components-override/uiterwaarde/steps/book/receipt-confirm.vue'),
        ),
    );
}
